/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Organization Switcher Component
 * Allows users to switch between personal account and organization contexts
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useAuthStore } from '../stores/authStore'
import { useOrganizationStore } from '../stores/organizationStore'
import { Building2, User, Check, ChevronDown } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { canMeaningfullySwitchOrgs } from '../utils/orgSwitching'

const OrganizationSwitcher = ({ variant = 'dropdown', showOnlyNonFamily = false }) => {
  const { user } = useAuthStore()
  const { activeOrganization, setActiveOrganization, clearActiveOrganization } =
    useOrganizationStore()
  const [isOpen, setIsOpen] = useState(false)

  if (!user) return null

  const organizations = user.organizations || []

  // Hide entirely when switching wouldn't change anything for this user (e.g. a
  // plain student whose only non-personal org is a family). Keeps the profile
  // Organization tab from showing a lone, pointless workspace card.
  if (!canMeaningfullySwitchOrgs(organizations)) return null

  // Determine if user should see Personal Account option
  // Only show if:
  // 1. User has a personal organization, OR
  // 2. User has no organizations at all (edge case)
  const personalOrg = organizations.find((org) => org.type === 'personal')
  const hasPersonalOrg = Boolean(personalOrg)
  const shouldShowPersonalAccount = hasPersonalOrg || organizations.length === 0
  // Show the personal workspace by its actual name when it has one, falling back
  // to the generic label (e.g. brand-new users with no personal org yet).
  const personalLabel = personalOrg?.name || 'Personal Account'

  // Filter out personal orgs from the list since they're shown as "Personal Account"
  // Also filter out family orgs if showOnlyNonFamily is true (family is handled by FamilyMemberManagement)
  const nonPersonalOrgs = organizations.filter((org) => {
    if (org.type === 'personal') return false
    if (showOnlyNonFamily && org.type === 'family') return false
    return true
  })
  const hasNonPersonalOrgs = nonPersonalOrgs.length > 0

  // Don't render if showOnlyNonFamily and there are no non-family orgs to show
  if (showOnlyNonFamily && !hasNonPersonalOrgs && !shouldShowPersonalAccount) {
    return null
  }

  // Personal mode = no active org, or a personal-type org is active (some flows set
  // the personal org object rather than null). Both mean "Personal Account".
  const inPersonalMode = !activeOrganization || activeOrganization.type === 'personal'

  const handleSwitch = (org) => {
    // Skip a no-op re-select so we don't re-POST the same preference to the server.
    if (org === null) {
      if (!inPersonalMode) {
        clearActiveOrganization()
        toast.success(`Switched to ${personalLabel}`)
      }
    } else if (activeOrganization?.id !== org.id) {
      setActiveOrganization(org)
      toast.success(`Switched to ${org.name}`)
    }
    setIsOpen(false)
  }

  const isActive = (org) => {
    if (org === null) return inPersonalMode
    return !inPersonalMode && activeOrganization?.id === org.id
  }

  const getOrganizationIcon = (type) => {
    return <Building2 className="w-4 h-4" />
  }

  const getRoleBadge = (role) => {
    const badges = {
      admin:
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
      teacher:
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      guardian:
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      student:
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
    }
    return badges[role] || badges.student
  }

  // Dropdown variant (for header/nav)
  if (variant === 'dropdown') {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-3 py-2 text-sm rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors w-full"
        >
          <div className="flex items-center space-x-2 flex-1">
            {inPersonalMode ? (
              <>
                <User className="w-4 h-4" />
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {personalLabel}
                </span>
              </>
            ) : (
              <>
                {getOrganizationIcon(activeOrganization.type)}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {activeOrganization.name}
                </span>
              </>
            )}
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
            {/* Personal Account - Only show if user should have one */}
            {shouldShowPersonalAccount && (
              <button
                onClick={() => handleSwitch(null)}
                className={`flex items-center justify-between w-full px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  isActive(null) ? 'bg-gray-50 dark:bg-gray-700' : ''
                }`}
              >
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <span className="text-gray-900 dark:text-gray-100">{personalLabel}</span>
                </div>
                {isActive(null) && <Check className="w-4 h-4 text-primary-600" />}
              </button>
            )}

            {/* Organizations (excluding personal orgs which are shown above) */}
            {hasNonPersonalOrgs && (
              <>
                {shouldShowPersonalAccount && (
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                )}
                <div className="px-3 py-1">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Organizations
                  </p>
                </div>
                {nonPersonalOrgs.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => handleSwitch(org)}
                    className={`flex items-center justify-between w-full px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      isActive(org) ? 'bg-gray-50 dark:bg-gray-700' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-2 flex-1">
                      {getOrganizationIcon(org.type)}
                      <div className="flex flex-col items-start">
                        <span className="text-gray-900 dark:text-gray-100">{org.name}</span>
                        <span className={getRoleBadge(org.role)}>{org.role}</span>
                      </div>
                    </div>
                    {isActive(org) && <Check className="w-4 h-4 text-primary-600" />}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  // Card variant (for profile page)
  return (
    <div className="space-y-3">
      {/* Personal Account - Only show if user should have one */}
      {shouldShowPersonalAccount && (
        <button
          onClick={() => handleSwitch(null)}
          className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
            isActive(null)
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
        >
          <div className="flex items-center space-x-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isActive(null)
                  ? 'bg-primary-100 dark:bg-primary-800'
                  : 'bg-gray-100 dark:bg-gray-800'
              }`}
            >
              <User
                className={`w-5 h-5 ${isActive(null) ? 'text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'}`}
              />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-900 dark:text-gray-100">{personalLabel}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Your individual workspace</p>
            </div>
          </div>
          {isActive(null) && (
            <Check className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
          )}
        </button>
      )}

      {/* Organizations (excluding personal orgs which are shown above) */}
      {hasNonPersonalOrgs && shouldShowPersonalAccount && (
        <div className="flex items-center space-x-2">
          <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            Organizations
          </span>
          <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
        </div>
      )}
      {hasNonPersonalOrgs && (
        <>
          {nonPersonalOrgs.map((org) => (
            <button
              key={org.id}
              onClick={() => handleSwitch(org)}
              className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                isActive(org)
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isActive(org)
                      ? 'bg-primary-100 dark:bg-primary-800'
                      : 'bg-gray-100 dark:bg-gray-800'
                  }`}
                >
                  {getOrganizationIcon(org.type)}
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-gray-100">{org.name}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={getRoleBadge(org.role)}>{org.role}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{org.type}</span>
                  </div>
                </div>
              </div>
              {isActive(org) && (
                <Check className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
              )}
            </button>
          ))}
        </>
      )}
    </div>
  )
}

export default OrganizationSwitcher
